import { auth } from 'api/AuthAPI'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAuthUser } from 'reduxStore/auth/selectors'
import { replayOfflineSaves } from 'reduxStore/projects/thunks'
import { OUTBOX_CHANGED, queuedSaves } from 'utils/saveOutbox'

type ThunkDispatch = (thunk: unknown) => Promise<unknown>

const useOnline = () => {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])
  return online
}

/** Whether the device is online, and how many of this user's saves are waiting on it. */
export function useOfflineStatus() {
  const online = useOnline()
  const uid = useSelector(selectAuthUser)?.uid
  const [pending, setPending] = useState(0)

  useEffect(() => {
    if (!uid) return
    let live = true
    const count = () =>
      queuedSaves(String(uid))
        .then((saves) => live && setPending(saves.length))
        .catch(() => undefined)
    count()
    window.addEventListener(OUTBOX_CHANGED, count)
    return () => {
      live = false
      window.removeEventListener(OUTBOX_CHANGED, count)
    }
  }, [uid])

  return { online, pending }
}

/** Replays offline saves on sign-in and whenever the connection comes back. Mount once, at the app root. */
export function useOfflineReplay() {
  const dispatch = useDispatch() as ThunkDispatch
  const online = useOnline()
  const uid = useSelector(selectAuthUser)?.uid

  const replay = useCallback(async () => {
    // The stored token may have expired offline; a stale one would 401 and sign the user out mid-replay.
    try {
      const token = await auth.currentUser?.getIdToken()
      if (token) localStorage.setItem('token', token)
    } catch {
      return
    }
    await dispatch(replayOfflineSaves())
  }, [dispatch])

  useEffect(() => {
    if (online && uid) void replay()
  }, [online, uid, replay])
}
